import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Progress } from './progress';
import { Card } from './card';
import { Users, Clock, CheckCircle, AlertCircle, Briefcase } from 'lucide-react';

const funTeamSteps = [
  "Stalking LinkedIn profiles in a totally professional way 🕵️",
  "Calculating founder-market fit using advanced algorithms",
  "Checking if they've exited before (preferably to a FAANG) 💸",
  "Analyzing their Twitter follower count for thought leadership",
  "Verifying they went to Stanford, MIT, or dropped out of Harvard",
  "Counting how many times they've pivoted (optimal: 2-3)",
  "Measuring their coffee consumption to productivity ratio ☕",
  "Checking if they've been on any 'Forbes 30 Under 30' lists",
  "Calculating their combined years of 'crushing it' experience",
  "Determining if they can explain their startup in an elevator pitch",
  "Analyzing their GitHub commit history for 10x developer status",
  "Checking if they've survived a previous startup winter ❄️",
  "Evaluating their meme game on company Slack",
  "Verifying they have the perfect technical/business co-founder ratio",
  "Assessing their ability to raise funding on vibes alone",
  "Checking if they've given a TED talk (or at least TEDx)",
  "Measuring their resilience by past rejection emails saved",
  "Calculating their burn rate tolerance threshold",
  "Determining if they own a Patagonia vest (crucial for VCs)",
  "Analyzing their network effect potential on LinkedIn",
];

interface TeamAnalysisProgressCardProps {
  status: 'pending' | 'processing' | 'completed' | 'failed';
  createdAt?: string;
  onComplete?: () => void;
  profileCount?: number;
}

export const TeamAnalysisProgressCard: React.FC<TeamAnalysisProgressCardProps> = ({ 
  status, 
  createdAt, 
  onComplete,
  profileCount = 1
}) => {
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  
  useEffect(() => {
    if (status === 'processing') {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          const increment = Math.random() * 2 + 1.5;
          const newProgress = Math.min(prev + increment, 95);
          return newProgress;
        });
      }, 2500);
      
      return () => clearInterval(progressInterval);
    } else if (status === 'completed') {
      setProgress(100);
      if (onComplete) {
        setTimeout(onComplete, 500);
      }
    }
  }, [status, onComplete]);
  
  useEffect(() => {
    // Change steps periodically
    if (status === 'processing' || status === 'pending') {
      const stepInterval = setInterval(() => {
        setCurrentStep(prev => (prev + 1) % funTeamSteps.length);
      }, 3500);
      
      return () => clearInterval(stepInterval);
    }
  }, [status]);
  
  useEffect(() => {
    // Calculate elapsed time
    if (createdAt && (status === 'processing' || status === 'pending')) {
      const startTime = new Date(createdAt).getTime();
      const interval = setInterval(() => {
        const now = new Date().getTime();
        const elapsed = Math.floor((now - startTime) / 1000);
        setElapsedTime(elapsed);
      }, 1000);
      
      return () => clearInterval(interval);
    }
  }, [createdAt, status]);
  
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  const getStatusIcon = () => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="text-green-600" size={24} />;
      case 'failed':
        return <AlertCircle className="text-red-600" size={24} />;
      default:
        return <Users className="text-purple-600 animate-pulse" size={24} />;
    }
  };
  
  const getStatusColor = () => {
    switch (status) {
      case 'completed':
        return 'border-green-200 bg-green-50/50';
      case 'failed':
        return 'border-red-200 bg-red-50/50';
      case 'processing':
        return 'border-purple-200 bg-purple-50/50';
      default:
        return 'border-gray-200 bg-gray-50/50';
    }
  };
  
  return (
    <Card className={`p-6 transition-all duration-300 ${getStatusColor()}`}>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            {getStatusIcon()}
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Team Analysis
              </h3>
              <p className="text-sm text-gray-600">
                {status === 'pending' && `Analyzing ${profileCount} team member${profileCount > 1 ? 's' : ''}`}
                {status === 'processing' && 'Deep diving into founder profiles'}
                {status === 'completed' && 'Team analysis complete!'}
                {status === 'failed' && 'Analysis failed'}
              </p>
            </div>
          </div>
          
          {(status === 'processing' || status === 'pending') && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Clock size={16} />
              <span>{formatTime(elapsedTime)}</span>
            </div>
          )}
        </div>
        
        {/* Fun status message */}
        {(status === 'processing' || status === 'pending') && (
          <AnimatePresence mode="wait">
            <motion.p
              key={currentStep}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="text-sm text-gray-700 italic min-h-[20px]"
            >
              {funTeamSteps[currentStep]}
            </motion.p>
          </AnimatePresence>
        )}
        
        {/* Progress bar */}
        {(status === 'processing' || status === 'pending') && (
          <div className="space-y-2">
            <Progress value={progress} className="h-2" />
            <div className="flex justify-between text-xs text-gray-500">
              <span>{progress.toFixed(0)}% complete</span>
              <span>Est. 2-4 minutes</span>
            </div>
          </div>
        )}
        
        {/* Completion message */}
        {status === 'completed' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            className="flex items-center gap-2 text-green-700"
          >
            <CheckCircle size={20} />
            <span className="font-medium">Team analysis ready! Check out the insights.</span>
          </motion.div>
        )}
        
        {/* Error message */}
        {status === 'failed' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            className="flex items-center gap-2 text-red-700"
          >
            <AlertCircle size={20} />
            <span className="font-medium">Unable to analyze team profiles. Please try again.</span>
          </motion.div>
        )}
        
        {/* Background info */}
        <div className="text-xs text-gray-500 pt-2 border-t border-gray-200">
          <div className="flex items-center gap-1">
            <Briefcase size={12} />
            <p>
              {status === 'pending' && 'Your team analysis is queued'}
              {status === 'processing' && 'AI is evaluating founder-market fit and team dynamics'}
              {status === 'completed' && 'Team strengths, gaps, and recommendations are ready'}
              {status === 'failed' && 'Please check the LinkedIn URLs and try again'}
            </p>
          </div>
        </div>
      </div>
    </Card>
  );
};